import React from 'react';

export type Props = {
  Pager: (props: {
    selectedPageNumber: number;
    next:
      | {
          tag: 'canNext';
          next: () => void;
        }
      | {
          tag: 'cannotNext';
        };
    prev:
      | {
          tag: 'canPrev';
          prev: () => void;
        }
      | {
          tag: 'cannotPrev';
        };
    setSelectedPageNumber: (pageNumber: number) => void;
    maxPageNumber: number;
  }) => React.ReactElement;
  children: React.ReactElement[];
  numberOfItemsAtPage: number;
  listClassName: string;
  className?: string;
  selectedIndex?: number | null;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number | null>>;
};

export const PaginatedList = (props: Props): React.ReactElement => {
  const lengthOfPages = Math.ceil(
    props.children.length / props.numberOfItemsAtPage
  );
  const [selectedIndexInternal, setSelectedIndexInternal] = React.useState<
    number | null
  >(lengthOfPages > 0 ? 0 : null);
  const selectedIndex = props.selectedIndex ?? selectedIndexInternal;
  const setSelectedIndex = props.setSelectedIndex ?? setSelectedIndexInternal;
  const maxIndex = lengthOfPages === 0 ? null : lengthOfPages - 1;
  React.useEffect(() => {
    if (maxIndex === null || selectedIndex === null) return;
    if (selectedIndex > maxIndex) setSelectedIndex(maxIndex);
    if (selectedIndex < 0) setSelectedIndex(0);
  }, [maxIndex, selectedIndex, setSelectedIndex]);
  const minIndex = 0;
  const next: React.ComponentProps<typeof props['Pager']>['next'] =
    maxIndex !== null && selectedIndex !== null && selectedIndex < maxIndex
      ? {
          tag: 'canNext',
          next: (): void =>
            setSelectedIndex(prev =>
              prev !== null ? Math.min(maxIndex, prev + 1) : null
            ),
        }
      : {
          tag: 'cannotNext',
        };
  const prev: React.ComponentProps<typeof props['Pager']>['prev'] =
    selectedIndex !== null && selectedIndex > minIndex
      ? {
          tag: 'canPrev',
          prev: (): void =>
            setSelectedIndex(prev =>
              prev !== null ? Math.max(minIndex, prev - 1) : null
            ),
        }
      : {
          tag: 'cannotPrev',
        };
  return (
    <>
      <div className={`${props.className ?? ''}`}>
        <div className={props.listClassName}>
          {props.children.filter((_, i) => {
            return Math.floor(i / props.numberOfItemsAtPage) === selectedIndex;
          })}
        </div>
        {selectedIndex !== null && maxIndex !== null && (
          <props.Pager
            {...{
              selectedPageNumber: selectedIndex + 1,
              next,
              prev,
              setSelectedPageNumber: pageNumber =>
                setSelectedIndex(
                  Math.max(minIndex, Math.min(maxIndex, pageNumber - 1))
                ),
              maxPageNumber: maxIndex + 1,
            }}
          />
        )}
      </div>
    </>
  );
};
